// @flow
import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import values from 'lodash/values';
import TopBar from '../bars/top-bar';
import BackIconButton from '../buttons/BackIconButton';
// import ShareIconButton from '../buttons/ShareIconButton'
import ZoomInIconButton from '../buttons/ZoomInIconButton';
import AuctionStateTag from '../tags/auction-state-tag';
import ImgCover from '../utilities/img-cover';
import InnerSlider from '../sliders/inner-slider';
import HeadingLabel from '../headings/heading-label';
import HeadingDelta from '../headings/heading-delta';
import SmallText from '../texts/small-text';
import Indicator from '../texts/indicator';
import InfoBlock from '../texts/info-block';
import InfoText from '../texts/info-text';
import InfoExpanders from '../lists/info-expanders';
import ActionBox from '../../services/item/ActionBox';
import MapBox from '../../ui/maps/map-box';
import InfoExpandersItem from '../lists/info-expanders-item';
import MoreInfoBlock from '../texts/more-info-block';
import SelectModal from '../../services/modal/SelectModal';
import { Link } from 'react-router-dom';
import { bidShape, charityShape } from '../../services/prop-types';
import { ITEM_DETAIL_MODAL_TYPE } from '../../services/item/constants';

const createTopBarLeftChildren = props => {
  const { item } = props;
  const { auction } = item;
  const topBarLeftChildren = [<BackIconButton key='BackIconButton' />];
  if (auction && auction.state !== 'pending') {
    topBarLeftChildren.push(
      <AuctionStateTag auctionState={auction.state} key='AuctionStateTag' />
    );
  }
  return topBarLeftChildren;
};

const ItemDetail = (props: Object) => {
  const {
    item,
    charity,
    currentBid,
    isModalOpen,
    modalType,
    openInstructions,
    openQR,
    openBidHistory,
    openAboutShipping,
    openQuestions,
    openHowToBid,
    closeModal,
    translations,
    itemCountdownText,
    auctionLink
  } = props;
  const { auction } = item;

  return (
    <div>
      <TopBar
        theme='light-bar'
        leftChildren={createTopBarLeftChildren(props)}
        // rightChildren={[<ShareIconButton key={1} />]}
      />
      <MediaQuery query='(min-width: 1024px)'>
        <ActionBox
          item={item}
          currentBid={currentBid}
          itemCountdownText={itemCountdownText}
        />
      </MediaQuery>
      <div className='detail-item'>
        {item && item.media && item.media.length > 1 ? (
          <div className='detail-image'>
            <InnerSlider slides={item.media} active={0} />
            <ZoomInIconButton />
          </div>
        ) : (
          <div className='detail-image'>
            <ImgCover src={item.media[0]} />
          </div>
        )}
        <div className='detail-info'>
          <div className='detail-section desk-half'>
            <HeadingLabel labelText={item.category} titleText={item.title} />
            <SmallText light={true}>
              <Link to={auctionLink}>{auction.title} </Link>
            </SmallText>
            <Indicator
              className='shy-indicator'
              labelText={translations.itemNumber}
              titleText={item.itemNumber}
            />
            {charity && charity.name && (
              <Indicator
                className='shy-indicator'
                labelText={translations.charity}
                titleText={charity.name}
                url={`/charities/${charity.id}`}
              />
            )}
            <Indicator
              className='shy-indicator'
              labelText={translations.donor}
              titleText={item.donor}
            />
            {auction && auction.state === 'closed' && (
              <Indicator
                isLoud
                labelText={translations.finalBid}
                hasSmallText
                smallText='$'
                titleText={currentBid ? currentBid.amount : item.initialBid}
              />
            )}
          </div>
          <div className='detail-section'>
            <InfoBlock>
              <HeadingDelta>{translations.description}</HeadingDelta>
              <MoreInfoBlock limitLines={5} text={item.description} />
            </InfoBlock>
            {item.lat && item.lng && item.location && (
              <InfoBlock>
                <HeadingDelta>{translations.locationAndDate}</HeadingDelta>
                {item.location && (
                  <InfoText iconName='map-pin'>{item.location}</InfoText>
                )}
              </InfoBlock>
            )}
            {item && item.lat && item.lng && (
              <MapBox
                lat={item.lat}
                lng={item.lng}
                containerElement={<div className='map-box' id='map-box' />}
                mapElement={<div style={{ height: '100%' }} />}
              />
            )}
            {item && item.date && (
              <InfoBlock>
                {item.date
                  .split('\n')
                  .filter(p => p)
                  .map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
              </InfoBlock>
            )}
          </div>
          <InfoExpanders>
            {item && item.instructions && (
              <InfoExpandersItem
                href='#'
                onClick={openInstructions}
                iconName='instructions'
              >
                {translations.instructions}
              </InfoExpandersItem>
            )}
            <InfoExpandersItem href='#' onClick={openQR} iconName='config'>
              {translations.qrCode}
            </InfoExpandersItem>
            {auction && auction.state !== 'pending' && (
              <InfoExpandersItem
                href='#'
                onClick={openBidHistory}
                iconName='clock'
              >
                {translations.bids}
              </InfoExpandersItem>
            )}
            <InfoExpandersItem
              href='#'
              onClick={openAboutShipping}
              iconName='delivery'
            >
              {translations.aboutShipping}
            </InfoExpandersItem>
            {item && (item.info || auction.info) && (
              <InfoExpandersItem
                href='#'
                onClick={openQuestions}
                iconName='question'
              >
                {translations.question}
              </InfoExpandersItem>
            )}
            <InfoExpandersItem href='#' onClick={openHowToBid} iconName='info'>
              {translations.bidding}
            </InfoExpandersItem>
          </InfoExpanders>
        </div>
      </div>
      <MediaQuery query='(max-width: 1023px)'>
        <ActionBox
          item={item}
          className='fixed-bottom'
          itemCountdownText={itemCountdownText}
        />
      </MediaQuery>
      {isModalOpen && (
        <SelectModal onClose={closeModal} modalType={modalType} />
      )}
    </div>
  );
};

ItemDetail.propTypes = {
  currentBid: bidShape,
  item: PropTypes.object.isRequired,
  charity: charityShape,
  hasSlider: PropTypes.bool,
  isSaved: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  modalType: PropTypes.oneOf(values(ITEM_DETAIL_MODAL_TYPE)),
  openInstructions: PropTypes.func,
  openBidHistory: PropTypes.func,
  openAboutShipping: PropTypes.func,
  openQuestions: PropTypes.func,
  openHowToBid: PropTypes.func,
  closeModal: PropTypes.func,
  // translations
  translations: PropTypes.shape({
    itemNumber: PropTypes.string.isRequired,
    charity: PropTypes.string.isRequired,
    donor: PropTypes.string.isRequired,
    finalBid: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    locationAndDate: PropTypes.string.isRequired,
    instructions: PropTypes.string.isRequired,
    bids: PropTypes.string.isRequired,
    aboutShipping: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    bidding: PropTypes.string.isRequired
  }).isRequired,
  itemCountdownText: PropTypes.string.isRequired,
  auctionLink: PropTypes.string.isRequired
};

export default ItemDetail;



// WEBPACK FOOTER //
// ./src/ui/views/item-detail.js